import type { Insight as InsightData, InsightTag } from "../domain/insight";
import { useGo } from "../lib/useGo";
import { I } from "./icons";
import { RowGroup } from "./Rows";
import type { TagTone } from "./Tag";
import { Tag } from "./Tag";

const TAG_TONE: Record<InsightTag, TagTone> = {
  est: "est",
  cycle: "cycle",
  phone: "phone",
  console: "console",
  measured: "measured",
};

/**
 * One claim and its evidence (kit §23, ADR-033): the sentence (15/500 ink), then an evidence line
 * that always starts "based on" and names the count and window. `insight.tag` renders as a Tag at
 * the end of that line; `wait` marks a claim the data does not yet earn — the sentence turns
 * muted and a dashed `planned` Tag carries what unlocks it, alongside (not instead of) any
 * provenance tag.
 *
 * With `insight.link` the whole row is the button: a link inside the sentence was one line of
 * text tall, well under the 44px target. Its label trails the sentence in brand 600 with a
 * chevron, and nothing inside the row is interactive on its own.
 *
 * `first` drops the top rule — for the first row of a plain group (`InsightList` sets it). A
 * `Chapter`'s Insight rows never pass it: the leads sit above them, so even the first one needs
 * the divider (kit §24).
 */
export function Insight({ insight, first }: { insight: InsightData; first?: boolean }) {
  const navigate = useGo();
  const { link } = insight;
  const cls = `k-insight${first ? " k-insight--first" : ""}${insight.wait ? " k-insight--wait" : ""}${
    link ? " k-insight--link" : ""
  }`;
  const evidence = (
    <>
      <span>based on {insight.basedOn}</span>
      {insight.tag ? <Tag tone={TAG_TONE[insight.tag]} /> : null}
      {insight.wait ? <Tag tone="planned">{insight.wait}</Tag> : null}
    </>
  );
  if (!link)
    return (
      <div className={cls}>
        <p className="k-insight__text">{insight.text}</p>
        <p className="k-insight__evidence">{evidence}</p>
      </div>
    );
  // A button holds phrasing content only, so the two lines are spans laid out as blocks.
  return (
    <button type="button" className={cls} onClick={() => navigate(link.to)}>
      <span className="k-insight__text">
        {insight.text}{" "}
        <span className="k-insight__link">
          {link.label}
          {I["chevron-right"]}
        </span>
      </span>
      <span className="k-insight__evidence">{evidence}</span>
    </button>
  );
}

/**
 * Insight rows in order, inside one RowGroup (kit §23). `firstFlush` (default true) drops the top
 * rule from the first row — set it `false` only when this list is not the first thing in its group.
 */
export function InsightList({ insights, firstFlush = true }: { insights: InsightData[]; firstFlush?: boolean }) {
  return (
    <RowGroup>
      {insights.map((insight, i) => (
        <Insight key={insight.key} insight={insight} first={i === 0 && firstFlush} />
      ))}
    </RowGroup>
  );
}
